export type CancellationConfirmSummary = {
  appointmentStatus?: string;
  hoursUntilAppointment?: number;
  cancellationFee?: number;
  refundPercentage?: number;
  gracePeriodApplied?: boolean;
  message?: string;
};

function formatTimeUntil(hoursUntil?: number): string {
  const hours = Math.floor(hoursUntil || 0);
  const minutes = Math.round(((hoursUntil || 0) - hours) * 60);
  return `${hours}h ${minutes}m`;
}

/**
 * Client-facing cancel-confirm copy for the deferred-charge model.
 * Describes a charge (or no charge), never a refund of money already taken.
 */
export function formatCancellationConfirmBody(summary: CancellationConfirmSummary): string {
  const isPending = summary.appointmentStatus === 'pending';
  let body = isPending
    ? `Your appointment is still pending—the stylist hasn't confirmed it yet. No booking guarantee will be charged and no cancellation fee will be applied.`
    : summary.message || 'A cancellation fee may apply depending on the stylist\'s policy.';

  body += `\n\n⏰ Time until appointment: ${formatTimeUntil(summary.hoursUntilAppointment)}`;
  return body;
}

export function formatCancellationSuccessMessage(summary: CancellationConfirmSummary): string {
  if (summary.appointmentStatus === 'pending') {
    return 'Appointment cancelled.';
  }
  const fee = Number(summary.cancellationFee);
  if (summary.gracePeriodApplied || !Number.isFinite(fee) || fee <= 0) {
    return 'Appointment cancelled. No cancellation fee applies.';
  }
  return `Appointment cancelled. A $${fee.toFixed(2)} cancellation fee applies.`;
}

export function cancellationConfirmButtonLabel(
  summary: CancellationConfirmSummary,
  feeButton = 'Cancel Anyway',
  defaultButton = 'Yes, Cancel'
): string {
  if (summary.appointmentStatus === 'pending') {
    return defaultButton;
  }
  if ((summary.cancellationFee ?? 0) > 0 && summary.refundPercentage === 0) {
    return feeButton;
  }
  return defaultButton;
}

export type CancellationRuleCopyInput = {
  hoursBeforeAppointment?: number;
  refundPercentage?: number;
  cancellationFeePercentage?: number;
  description?: string;
  rationale?: string;
  /** Next-higher tier's hours, used to build "12-24" / "less than 6" windows. */
  windowUpperHours?: number | null;
};

export type CancellationRuleCopy = {
  hoursBeforeAppointment: number;
  refundPercentage: number;
  cancellationFeePercentage: number;
  description: string;
  rationale?: string;
};

const OLD_REFUND_DESCRIPTION =
  /^(?:Full refund|No refund|\d+% refund) if cancelled /i;
const GENERATED_FEE_DESCRIPTION =
  /^(?:No cancellation fee|Quoted booking guarantee charged in full|\d+% of quoted (?:booking guarantee|down payment) charged) if cancelled /i;
const OLD_REFUND_RATIONALE =
  /^(?:Always some refund available for premium clients|Last chance for partial refund)$/i;

function cancellationFeePercent(rule: CancellationRuleCopyInput): number {
  return (
    rule.cancellationFeePercentage ??
    (typeof rule.refundPercentage === 'number' ? 100 - rule.refundPercentage : 0)
  );
}

export function windowUpperHoursFor(
  hoursBeforeAppointment: number,
  allHours: number[]
): number | null {
  const higher = allHours.filter((h) => h > hoursBeforeAppointment).sort((a, b) => a - b);
  return higher.length ? higher[0] : null;
}

export function describeCancellationRuleWindow(
  hoursBeforeAppointment: number,
  windowUpperHours?: number | null
): string {
  const hours = hoursBeforeAppointment ?? 0;
  if (hours <= 0) {
    return windowUpperHours != null && windowUpperHours > 0
      ? `less than ${windowUpperHours} hours before appointment`
      : 'less than the appointment time';
  }
  let topLabel = `${hours}+ hours`;
  if (hours > 48 && hours % 24 === 0) {
    topLabel = `${hours}+ hours (${hours / 24} days)`;
  }
  if (windowUpperHours == null) {
    return `${topLabel} before appointment`;
  }
  return `${hours}-${windowUpperHours} hours before appointment`;
}

export function generateCancellationRuleDescription(rule: CancellationRuleCopyInput): string {
  const fee = cancellationFeePercent(rule);
  const window = describeCancellationRuleWindow(
    rule.hoursBeforeAppointment ?? 0,
    rule.windowUpperHours
  );
  if (fee <= 0) {
    return `No cancellation fee if cancelled ${window}`;
  }
  if (fee >= 100) {
    return `Quoted booking guarantee charged in full if cancelled ${window}`;
  }
  return `${fee}% of quoted booking guarantee charged if cancelled ${window}`;
}

export function generateCancellationRuleRationale(rule: CancellationRuleCopyInput): string {
  const fee = cancellationFeePercent(rule);
  if (fee <= 0) {
    return 'Gives the stylist time to fill the slot';
  }
  if (fee >= 100) {
    return 'Too late to fill the slot';
  }
  return 'Short notice makes it harder to fill the slot';
}

export function isDefaultCancellationRuleDescription(text?: string): boolean {
  const value = (text || '').trim();
  if (!value) {
    return true;
  }
  return OLD_REFUND_DESCRIPTION.test(value) || GENERATED_FEE_DESCRIPTION.test(value);
}

export function isDefaultCancellationRuleRationale(text?: string): boolean {
  const value = (text || '').trim();
  if (!value) {
    return true;
  }
  return OLD_REFUND_RATIONALE.test(value);
}

export function refreshGeneratedCancellationRuleCopy<T extends CancellationRuleCopy>(
  rules: T[]
): T[] {
  const allHours = rules.map((rule) => rule.hoursBeforeAppointment);
  return rules.map((rule) => {
    const windowUpperHours = windowUpperHoursFor(rule.hoursBeforeAppointment, allHours);
    const input: CancellationRuleCopyInput = { ...rule, windowUpperHours };
    return {
      ...rule,
      description: isDefaultCancellationRuleDescription(rule.description)
        ? generateCancellationRuleDescription(input)
        : rule.description,
      rationale: isDefaultCancellationRuleRationale(rule.rationale)
        ? generateCancellationRuleRationale(input)
        : rule.rationale,
    };
  });
}

export function clientFacingCancellationFeeLabel(rule: {
  refundPercentage?: number;
  cancellationFeePercentage?: number;
}): string {
  return `${cancellationFeePercent(rule)}% fee`;
}

export function clientFacingCancellationRuleDescription(
  rule: CancellationRuleCopyInput
): string {
  if (
    rule.description &&
    !/refund/i.test(rule.description) &&
    !isDefaultCancellationRuleDescription(rule.description)
  ) {
    return rule.description;
  }
  return generateCancellationRuleDescription(rule);
}
